import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import PlaylistThumbnailBlank from '../components/TrackCard/Thumbnail/PlaylistThumbnailBlank';
import { PlaylistAssignmentsMonitoringRoutes } from '../features/playlistAssignments';
import { PlaylistDetails, PlaylistPageHeader, usePlaylist } from '../features/savedItems';
import { PlaylistItems, usePlaylistSearchManagerOpenState } from '../features/playlistItems';
import styled, { academyPageFullWidth, css, mediaQuery, useMediaQuery } from 'academy-common-lib/utils/Styling';
import { safeSmoothScrollTo } from '../utils/utils';
import { SavePlaylistTooltip } from '../features/myPlaylists';
import { ScrollIntoView } from '../features/routing';
import useWootricSurvey from '../hooks/useWootricSurvey';
import PlaylistPermissionsWarningMessage from '../features/savedItems/components/PlaylistPermissionsWarningMessage';
import PlaylistNotFound from '../features/savedItems/components/PlaylistNotFound';
import { useSetLastClickedMLRecommendationItemFromUrl } from '../features/recommendations/recommendationsHooks';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { LearningContentCard } from '../features/cards/components/LearningContentCard';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useHasGate } from '../features/accounts/accountsQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AcademyWidthSection = styled.div.withConfig({
  displayName: "Playlist__AcademyWidthSection",
  componentId: "sc-1rgga6m-0"
})(["", " padding:0 16px;", ""], academyPageFullWidth, mediaQuery('notMobile', css(["padding:0 64px;"])));
const PlaylistItemsContainer = styled(AcademyWidthSection).withConfig({
  displayName: "Playlist__PlaylistItemsContainer",
  componentId: "sc-1rgga6m-1"
})(["padding:76px 16px;", ""], mediaQuery('notMobile', css(["padding:0 64px;"])));
const TwoColumnSection = styled(AcademyWidthSection).withConfig({
  displayName: "Playlist__TwoColumnSection",
  componentId: "sc-1rgga6m-2"
})(["margin-top:52px;gap:0;display:flex;flex-direction:column;", ""], mediaQuery('notTablet', css(["display:grid;grid-template-columns:1fr 1fr;gap:24px;"])));
const BackgroundColoredSectionContent = styled.div.withConfig({
  displayName: "Playlist__BackgroundColoredSectionContent",
  componentId: "sc-1rgga6m-3"
})(["", ""], academyPageFullWidth);
const Playlist = () => {
  const {
    isMobile
  } = useMediaQuery();
  useWootricSurvey('CSAT');
  const {
    playlistId
  } = useParams();
  const id = parseInt(playlistId, 10);
  const {
    playlistError,
    playlist,
    playlistLoading
  } = usePlaylist(id);
  const searchInputRef = useRef(null);
  const playlistItemsRef = useRef(null);
  const {
    isPlaylistSearchManagerOpen,
    openPlaylistSearchManager
  } = usePlaylistSearchManagerOpenState();
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: [{
      id: (playlist === null || playlist === void 0 ? void 0 : playlist.id) || 0,
      type: (playlist === null || playlist === void 0 ? void 0 : playlist.type) === 'STANDARD' ? 'PLAYLIST' : 'LEARNING_PATH'
    }],
    skip: !playlist
  });
  const playlistCardModel = cardModels === null || cardModels === void 0 ? void 0 : cardModels[0];
  const isLoading = playlistLoading || cardModelsLoading;
  useSetLastClickedMLRecommendationItemFromUrl({
    id,
    type: 'PLAYLIST'
  });
  const onAddContent = () => {
    if (!searchInputRef || !searchInputRef.current || !playlistItemsRef.current) return;
    safeSmoothScrollTo(playlistItemsRef.current, 0, 0);
    if (!isPlaylistSearchManagerOpen) openPlaylistSearchManager();
    searchInputRef.current.focus();
  };
  const {
    hasGate: isUngatedForUnifiedAcademyNavigation
  } = useHasGate('Academy:UnifiedAcademy:Navigation');
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      justifyContent: "center",
      paddingBlockStart: "1000",
      size: "medium"
    });
  }
  if (playlistError || cardModelsError || !playlist || !playlistCardModel) {
    return /*#__PURE__*/_jsx(PlaylistNotFound, {});
  }
  return /*#__PURE__*/_jsxs("article", {
    children: [!isUngatedForUnifiedAcademyNavigation && /*#__PURE__*/_jsx(PlaylistPageHeader, {
      playlistId: id
    }), /*#__PURE__*/_jsx(PlaylistPermissionsWarningMessage, {
      playlistId: id
    }), /*#__PURE__*/_jsxs(TwoColumnSection, {
      children: [/*#__PURE__*/_jsx(LearningContentCard, {
        body: null,
        disableLink: true,
        item: playlistCardModel,
        renderMenu: menu => /*#__PURE__*/_jsx(SavePlaylistTooltip, {
          playlistId: id,
          children: menu
        }),
        thumbnailBody: /*#__PURE__*/_jsx(PlaylistThumbnailBlank, {
          onAddContent: onAddContent,
          playlist: playlistCardModel
        }),
        title: null,
        use: "on-dark"
      }), /*#__PURE__*/_jsx(PlaylistDetails, {
        playlistId: id
      })]
    }), /*#__PURE__*/_jsx(PlaylistItemsContainer, {
      ref: playlistItemsRef,
      children: /*#__PURE__*/_jsx(PlaylistItems, {
        playlistId: id,
        searchInputRef: searchInputRef
      })
    }), !isPlaylistSearchManagerOpen && /*#__PURE__*/_jsx(ScrollIntoView, {
      id: "playlist-assignments",
      children: /*#__PURE__*/_jsx(Flex, {
        backgroundColor: "fill-surface-recessed",
        inlineSize: "100%",
        justifyContent: "center",
        marginBlockStart: "1000",
        overflow: "hidden",
        children: /*#__PURE__*/_jsx(BackgroundColoredSectionContent, {
          children: /*#__PURE__*/_jsx(View, {
            paddingBlockEnd: isMobile ? '1400' : '1200',
            paddingBlockStart: isMobile ? '300' : '500',
            paddingInline: isMobile ? '0' : '1400',
            children: /*#__PURE__*/_jsx(PlaylistAssignmentsMonitoringRoutes, {})
          })
        })
      })
    })]
  });
};
export default Playlist;